import React, { useEffect, useState, useRef } from "react";
import api from "../utils/axios";
import CustomLoader from "./CustomLoader";
import { FiCalendar, FiSearch } from "react-icons/fi";

interface Transaction {
  id: number;
  admin_id: number;
  user_id: number;
  amount: number;
  type: string;
  remarks: string;
  recipient_type: string;
  performer_name: string | null;
  performer_role: string | null;
  recipient_name: string | null;
  recipient_role: string | null;
  created_at: string;
  performer_before_balance?: number | null;
  performer_after_balance?: number | null;
  receiver_before_balance?: number | null;
  receiver_after_balance?: number | null;
  user_recipient_name?: string | null;
  player_agent_username?: string | null;
  player_branch_username?: string | null;
  agent_parent_username?: string | null;
  performer_parent_username?: string | null;
}

interface PointTransactionReportProps {
  role?: "backoffice" | "agent" | "user";
}

const PointTransactionReport: React.FC<PointTransactionReportProps> = ({ role }) => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${yyyy}-${mm}-01`;
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${yyyy}-${mm}-${lastDay}`;
  });

  const startPickerRef = useRef<HTMLInputElement>(null);
  const endPickerRef = useRef<HTMLInputElement>(null);

  // Re-fetch when page changes
  useEffect(() => {
    fetchTransactionsWithParams(page, searchTerm, startDate, endDate, limit);
  }, [page]);

  // Reset page and filters when role changes
  useEffect(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const startStr = `${yyyy}-${mm}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const endStr = `${yyyy}-${mm}-${lastDay}`;

    setSearchTerm("");
    setStartDate(startStr);
    setEndDate(endStr);
    setLimit(10);
    setTypeFilter("");
    
    if (page === 1) {
      fetchTransactionsWithParams(1, "", startStr, endStr, 10, "");
    } else {
      setPage(1);
    }
  }, [role]);

  const fetchTransactionsWithParams = async (
    pageNum: number,
    searchVal: string,
    startVal: string,
    endVal: string,
    limitVal: number,
    typeVal?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const activeType = typeVal !== undefined ? typeVal : typeFilter;
      const response = await api.get(`/userCreditTransactions`, {
        params: {
          page: pageNum,
          pageSize: limitVal,
          search: searchVal.trim() || undefined,
          startDate: startVal || undefined,
          endDate: endVal || undefined,
          role: role || undefined,
          type: activeType || undefined,
        },
      });

      const { data, pagination } = response.data;
      setTransactions(data || []);
      setTotalPages(pagination?.totalPages || 1);
      setTotalRecords(pagination?.totalRecords || 0);
      setPage(pagination?.currentPage || 1);
    } catch (err: any) {
      console.error("Failed to fetch transaction history:", err);
      setError("Failed to fetch transaction history.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (page === 1) {
      fetchTransactionsWithParams(1, searchTerm, startDate, endDate, limit);
    } else {
      setPage(1);
    }
  };

  const handleClear = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const startStr = `${yyyy}-${mm}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const endStr = `${yyyy}-${mm}-${lastDay}`;

    setSearchTerm("");
    setStartDate(startStr);
    setEndDate(endStr);
    setTypeFilter("");
    
    if (page === 1) {
      fetchTransactionsWithParams(1, "", startStr, endStr, limit, "");
    } else {
      setPage(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    if (page === 1) {
      fetchTransactionsWithParams(1, searchTerm, startDate, endDate, newLimit);
    } else {
      setPage(1);
    }
  };

  const handleTypeChange = (newType: string) => {
    setTypeFilter(newType);
    if (page === 1) {
      fetchTransactionsWithParams(1, searchTerm, startDate, endDate, limit, newType);
    } else {
      setPage(1);
    }
  };

  const handleQuickSearch = (btnType: string) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    const formatDateString = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    if (btnType === "Today") {
      start = now;
      end = now;
    } else if (btnType === "Yesterday") {
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      start = yesterday;
      end = yesterday;
    } else if (btnType === "This Week") {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(now.setDate(diff));
      end = new Date();
    } else if (btnType === "Last Week") {
      const day = now.getDay();
      const diff = now.getDate() - day - 6;
      const lastMonday = new Date(now.setDate(diff));
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      start = lastMonday;
      end = lastSunday;
    } else if (btnType === "This Month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (btnType === "Last Month") {
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0);
    }

    const startStr = formatDateString(start);
    const endStr = formatDateString(end);

    setStartDate(startStr);
    setEndDate(endStr);

    if (page === 1) {
      fetchTransactionsWithParams(1, searchTerm, startStr, endStr, limit);
    } else {
      setPage(1);
    }
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  // Sum calculations for the footer
  let totalWithdrawn = 0;
  let totalDeposited = 0;

  transactions.forEach((item) => {
    if (item.type === "debited") {
      totalWithdrawn += Number(item.amount);
    } else {
      totalDeposited += Number(item.amount);
    }
  });

  const startIndex = transactions.length > 0 ? (page - 1) * limit + 1 : 0;
  const endIndex = (page - 1) * limit + transactions.length;



  return (
    <div className="min-h-screen space-y-6 p-4 bg-[#f8f9fa]">

      {/* Filters Section */}
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {/* Column 1: Dates */}
          <div className="space-y-3.5">
            {/* From Date */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-600 w-20 flex-shrink-0">From Date</label>
              <div className="flex items-center relative flex-1">
                <input
                  type="text"
                  readOnly
                  onClick={() => startPickerRef.current?.showPicker()}
                  className="border border-gray-300 rounded-l px-3 py-1.5 text-sm cursor-pointer bg-white focus:outline-none w-full h-9"
                  value={startDate ? `${startDate} 00:00:00` : ""}
                />
                <input
                  type="date"
                  ref={startPickerRef}
                  onChange={(e) => setStartDate(e.target.value)}
                  value={startDate}
                  className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none"
                />
                <button
                  type="button"
                  className="bg-gray-100 border-y border-r border-gray-300 px-3 py-1.5 rounded-r h-9 flex items-center justify-center text-gray-600 hover:bg-gray-200"
                  onClick={() => startPickerRef.current?.showPicker()}
                >
                  <FiCalendar size={16} />
                </button>
              </div>
            </div>

            {/* To Date */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-600 w-20 flex-shrink-0">To Date</label>
              <div className="flex items-center relative flex-1">
                <input
                  type="text"
                  readOnly
                  onClick={() => endPickerRef.current?.showPicker()}
                  className="border border-gray-300 rounded-l px-3 py-1.5 text-sm cursor-pointer bg-white focus:outline-none w-full h-9"
                  value={endDate ? `${endDate} 23:59:59` : ""}
                />
                <input
                  type="date"
                  ref={endPickerRef}
                  onChange={(e) => setEndDate(e.target.value)}
                  value={endDate}
                  className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none"
                />
                <button
                  type="button"
                  className="bg-gray-100 border-y border-r border-gray-300 px-3 py-1.5 rounded-r h-9 flex items-center justify-center text-gray-600 hover:bg-gray-200"
                  onClick={() => endPickerRef.current?.showPicker()}
                >
                  <FiCalendar size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Column 2: Username & Type */}
          <div className="space-y-3.5">
            {/* Username */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-600 w-20 flex-shrink-0">Username</label>
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-full h-9"
                  placeholder="Search by username"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
                <button
                  onClick={handleSearch}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold transition shadow-sm h-9 flex items-center gap-1 flex-shrink-0"
                >
                  <FiSearch size={14} /> Search
                </button>
              </div>
            </div>

            {/* Type */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-600 w-20 flex-shrink-0">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full h-9"
              >
                <option value="">All</option>
                <option value="credited">Deposit (In)</option>
                <option value="debited">Withdrawal (Out)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Quick Search */}
        <div className="space-y-2 pt-2 border-t border-gray-100">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Quick Search</div>
          <div className="flex flex-wrap gap-2">
            {["Last Month", "Last Week", "Yesterday", "Today", "This Week", "This Month"].map((btnLabel) => (
              <button
                key={btnLabel}
                onClick={() => handleQuickSearch(btnLabel)}
                className="px-4 py-2 bg-[#17a2b8] hover:bg-[#138496] text-white rounded text-sm font-semibold transition shadow-sm"
              >
                {btnLabel}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Show entries control */}
      <div className="flex justify-between items-center bg-white px-5 py-3 rounded-t-xl border-x border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span>Show</span>
          <select
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span>entries</span>
        </div>
        <button
          onClick={handleClear}
          className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm font-semibold transition"
        >
          Reset Filters
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 bg-white border border-gray-200 rounded-b-xl">
          <CustomLoader />
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-800 p-5 rounded-b-xl border border-red-200 text-center">
          {error}
        </div>
      ) : (
        <div className="bg-white border-x border-b border-gray-200 rounded-b-xl p-5 shadow-sm space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border border-gray-200">
              <thead className="bg-gray-50 text-gray-800 font-semibold border-b border-gray-200">
                <tr>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Date</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Account (Out)</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Account (In)</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Points Withdrawn</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Points Deposited</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Previous Balance</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Balance</th>
                  <th className="py-2.5 px-4 border border-gray-200 text-left">Execution Account</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-gray-700">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-gray-500 font-medium">
                      No point transactions recorded.
                    </td>
                  </tr>
                ) : (
                  transactions.map((item) => {
                    let performerName = item.performer_name 
                      ? `(${item.admin_id}) ${item.performer_name}` 
                      : `ID: ${item.admin_id}`;
                    let recipientName = item.recipient_name 
                      ? `(${item.user_id}) ${item.recipient_name}` 
                      : (item.user_recipient_name 
                        ? `(${item.user_id}) ${item.user_recipient_name}` 
                        : `ID: ${item.user_id}`);

                    if (item.remarks && item.remarks.includes(" | ")) {
                      const parts = item.remarks.split(" | ");
                      if (parts.length >= 3) {
                        const senderMatch = parts[0].match(/^Sender:\s*(.+?)\s*\((.+?)\)$/i);
                        const receiverMatch = parts[1].match(/^Receiver:\s*(.+?)\s*\((.+?)\)$/i);
                        if (senderMatch) performerName = `(${item.admin_id}) ${senderMatch[1]} (${senderMatch[2]})`;
                        if (receiverMatch) recipientName = `(${item.user_id}) ${receiverMatch[1]} (${receiverMatch[2]})`;
                      }
                    }

                    const dateFormatted = formatDateTime(item.created_at);

                    let accountOut = "";
                    let accountIn = "";
                    let pointsWithdrawn = 0;
                    let pointsDeposited = 0;

                    if (item.type === "debited") {
                      accountOut = recipientName;
                      accountIn = performerName;
                      pointsWithdrawn = item.amount;
                    } else {
                      accountOut = performerName;
                      accountIn = recipientName;
                      pointsDeposited = item.amount;
                    }

                    let receiverBefore: number | null = null;
                    let receiverAfter: number | null = null;
                    if (item.remarks && item.remarks.includes(" | ")) {
                      const parts = item.remarks.split(" | ");
                      if (parts.length >= 3 && parts[2] && parts[2].startsWith("Balances:")) {
                        const balMatch = parts[2].match(/^Balances:\s*(.+?),(.+?),(.+?),(.+?)$/);
                        if (balMatch) {
                          receiverBefore = parseFloat(balMatch[3]);
                          receiverAfter = parseFloat(balMatch[4]);
                        }
                      }
                    }

                    const finalPrevBal = receiverBefore !== null ? receiverBefore : (item.receiver_before_balance ?? null);
                    const finalAfterBal = receiverAfter !== null ? receiverAfter : (item.receiver_after_balance ?? null);

                    const getPerformerUpline = () => {
                      if (item.performer_role === 'agent' && item.performer_parent_username) {
                        return <div className="text-[10px] text-gray-400 font-normal">Upline: {item.performer_parent_username} (Branch)</div>;
                      }
                      return null;
                    };

                    const getRecipientUpline = () => {
                      if (item.recipient_role === 'agent' && item.agent_parent_username) {
                        return <div className="text-[10px] text-gray-400 font-normal">Upline: {item.agent_parent_username} (Branch)</div>;
                      }
                      const parts = [];
                      if (item.player_branch_username) parts.push(`${item.player_branch_username} (Branch)`);
                      if (item.player_agent_username) parts.push(`${item.player_agent_username} (Agent)`);
                      if (parts.length > 0) {
                        return <div className="text-[10px] text-gray-400 font-normal">Upline: {parts.join(" | ")}</div>;
                      }
                      return null;
                    };

                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition">
                        <td className="py-2.5 px-4 border border-gray-200 whitespace-nowrap">
                          {dateFormatted}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200 font-medium">
                          <div>{accountOut}</div>
                          {item.type === "debited" ? getRecipientUpline() : getPerformerUpline()}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200 font-medium">
                          <div>{accountIn}</div>
                          {item.type === "debited" ? getPerformerUpline() : getRecipientUpline()}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200">
                          {formatNumber(pointsWithdrawn)}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200">
                          {formatNumber(pointsDeposited)}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200">
                          {formatNumber(finalPrevBal)}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200 font-medium">
                          {formatNumber(finalAfterBal)}
                        </td>
                        <td className="py-2.5 px-4 border border-gray-200">
                          {performerName}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {transactions.length > 0 && (
                <tfoot className="bg-gray-50 text-gray-800 font-bold border-t border-gray-300">
                  <tr>
                    <td colSpan={3} className="py-2.5 px-4 border border-gray-200 text-right font-extrabold">
                      Total:
                    </td>
                    <td className="py-2.5 px-4 border border-gray-200 font-extrabold text-gray-900">
                      {formatNumber(totalWithdrawn)}
                    </td>
                    <td className="py-2.5 px-4 border border-gray-200 font-extrabold text-gray-900">
                      {formatNumber(totalDeposited)}
                    </td>
                    <td colSpan={3} className="py-2.5 px-4 border border-gray-200"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center pt-2">
            <div className="text-sm text-gray-600 font-medium">
              Showing {startIndex} to {endIndex} of {totalRecords} entries
            </div>
            <div className="flex gap-1">
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className={`px-3 py-1 bg-white border border-gray-300 rounded text-sm font-semibold transition ${
                  page === 1
                    ? "opacity-50 cursor-not-allowed text-gray-400"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pNum) => (
                <button
                  key={pNum}
                  onClick={() => handlePageChange(pNum)}
                  className={`px-3 py-1 border rounded text-sm font-semibold transition ${
                    page === pNum
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                  }`}
                >
                  {pNum}
                </button>
              ))}
              <button
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                className={`px-3 py-1 bg-white border border-gray-300 rounded text-sm font-semibold transition ${
                  page === totalPages
                    ? "opacity-50 cursor-not-allowed text-gray-400"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointTransactionReport;
